import type {
  CareStatePriorAuthStatusT,
  PriorAuthSummaryT,
  PriorAuthWorkflowStateT,
} from '@lotushealth/lotushealth_js_sdk/schemas';
import type { Tone } from '@/shared/ui/statusTone';

export type PriorAuthGroup = 'needs-you' | 'waiting' | 'settled';

interface PriorAuthCopy {
  label: string;
  description: string;
  actionLabel: string;
  tone: Tone;
}

const COPY: Record<CareStatePriorAuthStatusT, PriorAuthCopy> = {
  Requested: {
    label: 'Insurance reviewing',
    description: 'Insurance is reviewing this prior authorization.',
    actionLabel: 'View status',
    tone: 'info',
  },
  Appealing: {
    label: 'Appeal under review',
    description: 'Insurance is reviewing the appeal.',
    actionLabel: 'View appeal',
    tone: 'info',
  },
  'Cancel Requested': {
    label: 'Cancellation pending',
    description: 'Insurance is processing the cancellation.',
    actionLabel: 'View cancellation',
    tone: 'muted',
  },
  'Action Required': {
    label: 'Information needed',
    description: 'Insurance needs action before making a decision.',
    actionLabel: 'Review request',
    tone: 'warning',
  },
  Approved: {
    label: 'Approved',
    description: 'Insurance approved the medication. The prescription is ready to send.',
    actionLabel: 'Send prescription',
    tone: 'success',
  },
  'Partially Approved': {
    label: 'Approved with changes',
    description: 'Insurance approved a different quantity or duration.',
    actionLabel: 'Review decision',
    tone: 'warning',
  },
  'Not Required': {
    label: 'No authorization needed',
    description:
      'Insurance does not require prior authorization. The prescription is ready to send.',
    actionLabel: 'Send prescription',
    tone: 'neutral',
  },
  Denied: {
    label: 'Denied',
    description: 'Insurance denied this request. Review the reason and available options.',
    actionLabel: 'Review options',
    tone: 'destructive',
  },
  Cancelled: {
    label: 'Canceled',
    description: 'This prior authorization was canceled.',
    actionLabel: 'View summary',
    tone: 'muted',
  },
  Error: {
    label: 'Needs attention',
    description: 'This prior authorization could not be completed.',
    actionLabel: 'Review issue',
    tone: 'destructive',
  },
  'Sent to Pharmacy': {
    label: 'Sent to pharmacy',
    description: 'The prescription was sent to the pharmacy.',
    actionLabel: 'View summary',
    tone: 'success',
  },
  Superseded: {
    label: 'Medication changed',
    description: 'A different medication was chosen for this treatment.',
    actionLabel: 'View summary',
    tone: 'muted',
  },
  Ended: {
    label: 'Treatment ended',
    description: 'This medication is no longer part of the treatment plan.',
    actionLabel: 'View summary',
    tone: 'muted',
  },
};

const WORKFLOW_COPY: Record<PriorAuthWorkflowStateT, { label: string; description: string }> = {
  INITIATING: {
    label: 'Sending to insurance',
    description: 'We are sending this prior authorization to insurance.',
  },
  CONFIRMING_INITIATION: {
    label: 'Confirming receipt',
    description: 'We are confirming that insurance received this prior authorization.',
  },
  INITIATION_FAILED: {
    label: 'Could not send',
    description: 'This prior authorization could not be sent to insurance.',
  },
  QUESTION_REQUIRED: {
    label: 'Information needed',
    description: 'Insurance needs an answer before making a decision.',
  },
  ATTACHMENT_REQUIRED: {
    label: 'Document needed',
    description: 'Insurance needs a supporting document before making a decision.',
  },
  READY_TO_SUBMIT: {
    label: 'Ready to submit',
    description: 'All required information is complete. Submit it to insurance.',
  },
  SUBMISSION_FAILED: {
    label: 'Could not send',
    description: 'The completed prior authorization could not be sent to insurance.',
  },
  PAYER_REVIEW: {
    label: 'Insurance reviewing',
    description: 'Insurance is reviewing this prior authorization.',
  },
  APPEAL_REVIEW: {
    label: 'Appeal under review',
    description: 'Insurance is reviewing the appeal.',
  },
  CANCELLATION_REVIEW: {
    label: 'Cancellation pending',
    description: 'Insurance is processing the cancellation.',
  },
  APPROVED_READY_TO_SEND: {
    label: 'Approved',
    description: 'Insurance approved the medication. The prescription is ready to send.',
  },
  NOT_REQUIRED_READY_TO_SEND: {
    label: 'No authorization needed',
    description:
      'Insurance does not require prior authorization. The prescription is ready to send.',
  },
  PARTIAL_APPROVAL_DECISION: {
    label: 'Approved with changes',
    description: 'Insurance approved a different quantity or duration.',
  },
  DENIAL_DECISION: {
    label: 'Denied',
    description: 'Insurance denied this request. Review the reason and available options.',
  },
  PA_FAILED: {
    label: 'Needs attention',
    description: 'This prior authorization could not be completed.',
  },
  SENT: { label: 'Sent to pharmacy', description: 'The prescription was sent to the pharmacy.' },
  CANCELED: { label: 'Canceled', description: 'This prior authorization was canceled.' },
  SUPERSEDED: {
    label: 'Medication changed',
    description: 'A different medication was chosen for this treatment.',
  },
  ENDED: {
    label: 'Treatment ended',
    description: 'This medication is no longer part of the treatment plan.',
  },
};

const WAITING_STATUSES = new Set<CareStatePriorAuthStatusT>([
  'Requested',
  'Appealing',
  'Cancel Requested',
]);

const SETTLED_STATUSES = new Set<CareStatePriorAuthStatusT>([
  'Partially Approved',
  'Denied',
  'Cancelled',
  'Error',
  'Sent to Pharmacy',
  'Superseded',
  'Ended',
]);

export function groupPriorAuth(summary: PriorAuthSummaryT): PriorAuthGroup {
  if (summary.status && WAITING_STATUSES.has(summary.status)) {
    return 'waiting';
  }
  if (summary.status && SETTLED_STATUSES.has(summary.status)) {
    return 'settled';
  }
  return 'needs-you';
}

export function describePriorAuth(summary: PriorAuthSummaryT) {
  if (!summary.status) {
    return {
      label: 'Status unavailable',
      description: 'Open this prior authorization to see its latest status.',
      actionLabel: 'View details',
      tone: 'warning' as const,
    };
  }
  return COPY[summary.status];
}

function dateValue(value: string | null | undefined, fallback: number): number {
  if (!value) {
    return fallback;
  }
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? fallback : parsed;
}

export function sortPriorAuths(
  group: PriorAuthGroup,
  summaries: readonly PriorAuthSummaryT[]
): PriorAuthSummaryT[] {
  return [...summaries].sort((left, right) => {
    if (group === 'needs-you') {
      const leftDeadline = dateValue(left.nextResponseDeadline, Number.POSITIVE_INFINITY);
      const rightDeadline = dateValue(right.nextResponseDeadline, Number.POSITIVE_INFINITY);
      return leftDeadline === rightDeadline ? 0 : leftDeadline - rightDeadline;
    }
    return (
      dateValue(right.currentStatusDate ?? right.lastSeenAt, 0) -
      dateValue(left.currentStatusDate ?? left.lastSeenAt, 0)
    );
  });
}

export function formatMedicationName(
  displayName: string | null | undefined,
  strength: string | null | undefined
): string {
  const name = displayName?.trim() ?? '';
  const dose = strength?.trim() ?? '';
  if (!name) {
    return dose || 'Medication unavailable';
  }
  if (!dose || containsCompleteStrength(name, dose)) {
    return name;
  }
  return `${name} ${dose}`;
}

function containsCompleteStrength(displayName: string, strength: string): boolean {
  const name = displayName.normalize('NFKC').replace(/\s+/g, ' ').toLowerCase();
  const dose = strength.normalize('NFKC').replace(/\s+/g, ' ').toLowerCase();
  let index = name.indexOf(dose);

  while (index >= 0) {
    const before = name[index - 1];
    const after = name[index + dose.length];
    const startsAtBoundary = before === undefined || !/[a-z0-9]/.test(before);
    const endsAtBoundary = after === undefined || !/[a-z0-9/]/.test(after);
    if (startsAtBoundary && endsAtBoundary) {
      return true;
    }
    index = name.indexOf(dose, index + 1);
  }

  return false;
}

const HISTORY_COPY: Readonly<Record<string, string>> = {
  Unknown: 'Status requested from insurance',
  Requested: 'Sent to insurance',
  Pending: 'Received by insurance',
  InProcess: 'Insurance review started',
  Deferred: 'Insurance review paused',
  ActionRequired: 'Insurance requested more information',
  Approved: 'Approved by insurance',
  PartiallyDenied: 'Approved with changes',
  Denied: 'Denied by insurance',
  AppealRequested: 'Appeal sent to insurance',
  CancelRequested: 'Cancellation requested',
  Canceled: 'Prior authorization canceled',
  CancelDenied: 'Cancellation declined by insurance',
  NotRequired: 'Prior authorization not required',
  Closed: 'Insurance review completed',
  Error: 'Prior authorization could not be completed',
  Deleted: 'Prior authorization removed',
};

export function describePriorAuthHistoryStatus(status: string | null | undefined): string {
  if (!status) {
    return 'Status updated';
  }
  return HISTORY_COPY[status] ?? status.replace(/([a-z])([A-Z])/g, '$1 $2');
}

export function describePriorAuthWorkflow(workflowState: PriorAuthWorkflowStateT) {
  return WORKFLOW_COPY[workflowState];
}
